import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, FileDown, Hammer, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EmptyState, PageHeader, StatusBadge } from "@/components/shared/Common";
import { FilesPanel, StatusTimeline } from "@/components/shared/Records";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { OPP_STATUS, clp, fmtDate, fmtDateTime } from "@/lib/format";
import { OppForm, useLookups } from "./Sales";
import { QuoteDialog, StatusDialog, nextStatuses } from "./SaleDialogs";

const LABELS = Object.fromEntries(Object.entries(OPP_STATUS).map(([k, v]) => [k, v.label]));

const Quotes = ({ o, canEdit, onNew }) => {
  const pdf = async (q) => {
    try { const r = await api.get(`/gerencia/quotes/${q.id}/pdf`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="syc-card overflow-hidden" data-testid="quotes-panel">
      <div className="flex items-center justify-between px-5 py-3 border-b border-[#e4e8e1]">
        <span className="font-semibold text-sm">Cotizaciones ({o.quotes.length})</span>
        {canEdit && <Button data-testid="new-quote-btn" size="sm" className="rounded-full" onClick={onNew}><Plus size={14} className="mr-1" />Nueva versión</Button>}
      </div>
      {o.quotes.length === 0 ? <EmptyState title="Sin cotizaciones" text="Registre la primera versión para pasar a Cotizada." /> : o.quotes.map((q) => (
        <div key={q.id} className="flex flex-wrap items-center gap-4 px-5 py-3 border-b border-[#f0f2ee] text-sm" data-testid={`quote-row-${q.version}`}>
          <span className="font-mono font-bold">v{q.version}</span>
          <span>Neto {clp(q.net)}</span><span>IVA {clp(q.iva)}</span><b>Total {clp(q.total)}</b>
          <span className="text-[#5b6656]">Presupuesto {clp(q.budget_cost)}</span>
          <span className="text-xs text-[#8a9584]">{q.created_by_name} · {fmtDateTime(q.created_at)}</span>
          <Button data-testid={`quote-pdf-btn-${q.version}`} size="sm" variant="outline" className="ml-auto rounded-full" onClick={() => pdf(q)}><FileDown size={14} className="mr-1" />PDF</Button>
        </div>
      ))}
    </div>
  );
};

const Followups = ({ o, canEdit, onSaved }) => {
  const [f, setF] = useState({ note: "", next_action: "", next_date: "" });
  const add = async (e) => {
    e.preventDefault();
    try { await api.post(`/gerencia/opportunities/${o.id}/followups`, { ...f, next_date: f.next_date || null }); setF({ note: "", next_action: "", next_date: "" }); onSaved(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="syc-card overflow-hidden" data-testid="followups-panel">
      {canEdit && (
        <form onSubmit={add} className="grid sm:grid-cols-[1fr_200px_150px_auto] gap-2 p-4 border-b border-[#e4e8e1] bg-[#fafbf9]">
          <Input data-testid="followup-note-input" placeholder="Nota de seguimiento" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} required />
          <Input data-testid="followup-action-input" placeholder="Próxima acción" value={f.next_action} onChange={(e) => setF({ ...f, next_action: e.target.value })} />
          <Input data-testid="followup-date-input" type="date" value={f.next_date} onChange={(e) => setF({ ...f, next_date: e.target.value })} />
          <Button data-testid="add-followup-btn" className="rounded-full">Registrar</Button>
        </form>
      )}
      {o.followups.length === 0 ? <EmptyState title="Sin seguimientos" /> : o.followups.map((x) => (
        <div key={x.id} className="px-5 py-3 border-b border-[#f0f2ee] text-sm">
          <div>{x.note}</div>
          <div className="text-xs text-[#8a9584]">{x.user_name} · {fmtDateTime(x.created_at)}{x.next_action ? ` · Próxima: ${x.next_action} ${fmtDate(x.next_date)}` : ""}</div>
        </div>
      ))}
    </div>
  );
};

export default function SaleDetail() {
  const { id } = useParams();
  const { can } = useAuth();
  const navigate = useNavigate();
  const lookups = useLookups();
  const [o, setO] = useState(null);
  const [form, setForm] = useState(null);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [reasons, setReasons] = useState([]);
  const load = useCallback(() => api.get(`/gerencia/opportunities/${id}`).then((r) => { setO(r.data); setForm(r.data); }), [id]);
  useEffect(() => { load(); api.get("/catalogs/motivos_no_adjudicacion").then((r) => setReasons(r.data.map((i) => ({ id: i.code, name: i.name })))); }, [load]);
  if (!o) return <div className="text-[#5b6656]">Cargando…</div>;

  const locked = ["adjudicada", "no_adjudicada", "cancelada"].includes(o.status);
  const canEdit = can("gerencia.ventas.editar") && !locked;
  const st = OPP_STATUS[o.status];
  const save = async () => {
    const { title, client_id, contact_id, sales_owner_id, date, est_decision_date, description, notes } = form;
    try { await api.patch(`/gerencia/opportunities/${id}`, { title, client_id, contact_id, sales_owner_id, date, est_decision_date, description, notes }); toast.success("Venta actualizada"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const actions = (
    <>
      {can("gerencia.ventas.cambiar_estado") && nextStatuses(o.status).filter((s) => s !== "adjudicada" || can("gerencia.ventas.adjudicar")).map((s) => (
        <Button key={s} data-testid={`status-btn-${s}`} variant={s === "adjudicada" ? "default" : "outline"} className="rounded-full" onClick={() => setTarget(s)}>{OPP_STATUS[s].label}</Button>
      ))}
      {o.status === "adjudicada" && !o.ot_id && can("gerencia.ot.crear") && <Button data-testid="create-ot-from-sale-btn" className="rounded-full bg-[#5CD500] text-[#0f1a08] hover:bg-[#4fc000]" onClick={() => navigate(`/gerencia/ot/nueva?venta=${id}`)}><Hammer size={15} className="mr-1" />CREAR OT</Button>}
      {o.ot_id && <Link to={`/gerencia/ot/${o.ot_id}`} data-testid="sale-ot-link"><Button variant="outline" className="rounded-full">Ver {o.ot_code}</Button></Link>}
    </>
  );

  return (
    <div data-testid="sale-detail-page">
      <Link to="/gerencia/ventas" className="inline-flex items-center gap-1 text-sm font-semibold text-[#258F00] mb-4" data-testid="back-to-sales-link"><ArrowLeft size={15} />Ventas</Link>
      <PageHeader eyebrow={<span className="flex items-center gap-2 font-mono">{o.code} <StatusBadge testid="sale-status-badge" status={st.tone} label={st.label} /></span>} title={o.title}
        subtitle={`${o.client_name} · Neto ${clp(o.amount_net)} · IVA ${clp(o.iva)} · Total ${clp(o.total)}`} actions={actions} />
      {o.status === "no_adjudicada" && <div className="mb-5 rounded-xl border border-[#f5c2c0] bg-[#fdeaea] p-3 text-sm" data-testid="lost-reason-note">Motivo: <b>{reasons.find((r) => r.id === o.lost_reason)?.name || o.lost_reason}</b> {o.status_note}</div>}
      <Tabs defaultValue="cotizaciones">
        <TabsList className="mb-5 flex-wrap h-auto">
          <TabsTrigger value="cotizaciones" data-testid="sale-tab-quotes">Cotizaciones</TabsTrigger>
          <TabsTrigger value="seguimiento" data-testid="sale-tab-followups">Seguimiento</TabsTrigger>
          <TabsTrigger value="datos" data-testid="sale-tab-data">Datos</TabsTrigger>
          <TabsTrigger value="archivos" data-testid="sale-tab-files">Archivos</TabsTrigger>
          <TabsTrigger value="historial" data-testid="sale-tab-history">Historial</TabsTrigger>
        </TabsList>
        <TabsContent value="cotizaciones"><Quotes o={o} canEdit={canEdit} onNew={() => setQuoteOpen(true)} /></TabsContent>
        <TabsContent value="seguimiento"><Followups o={o} canEdit={can("gerencia.ventas.editar")} onSaved={load} /></TabsContent>
        <TabsContent value="datos"><div className="syc-card p-6 space-y-4 max-w-3xl"><fieldset disabled={!canEdit}><OppForm value={form} onChange={setForm} lookups={lookups} /></fieldset>{canEdit && <Button data-testid="save-sale-btn" className="rounded-full" onClick={save}>Guardar</Button>}</div></TabsContent>
        <TabsContent value="archivos"><FilesPanel resourceType="opportunity" resourceId={id} canWrite={can("gerencia.ventas.editar")} /></TabsContent>
        <TabsContent value="historial"><StatusTimeline items={[...o.status_history].reverse()} labels={LABELS} /></TabsContent>
      </Tabs>
      {quoteOpen && <QuoteDialog open onOpenChange={setQuoteOpen} oppId={id} base={o.quotes[0]} onSaved={() => { setQuoteOpen(false); load(); }} />}
      <StatusDialog target={target} onOpenChange={(v) => !v && setTarget(null)} oppId={id} reasons={reasons} onSaved={() => { setTarget(null); load(); }} />
    </div>
  );
}
